import { safeArrayAccess } from '@mimir/common/util/safeArrayAccess';
import type { Fraction } from '../../util/fractionType';
import { invariant } from '../../util/invariant';
import type { BerLength } from './types';

/* eslint-disable no-bitwise */
export interface SerializerTarget {
  writeByte(byte: number): void;
  writeBytes(array: Uint8Array): void;
  size(): number;
}

export class Serializer {
  constructor(private target: SerializerTarget) { }

  public writeByte(byte: number) {
    this.target.writeByte(byte);
  }

  public writeBytes(array: Uint8Array) {
    this.target.writeBytes(array);
  }

  public writeUInt8(value: number) {
    this.writeByte(value);
  }

  public writeInt8(value: number) {
    if (value < 0) {
      this.writeByte(256 + value);
    } else {
      this.writeByte(value);
    }
  }

  public writeUInt16BE(value: number) {
    this.writeUIntBE(value, 2);
  }

  public writeUInt32BE(value: number) {
    this.writeUIntBE(value, 4);
  }

  public writeUInt64BE(value: number) {
    this.writeUIntBE(value, 8);
  }

  public writeInt16BE(value: number) {
    this.writeIntBE(value, 2);
  }

  public writeInt32BE(value: number) {
    this.writeIntBE(value, 4);
  }

  public writeInt64BE(value: number) {
    this.writeIntBE(value, 8);
  }

  private writeUIntBE(value: number, byteCount: number) {
    do {
      byteCount -= 1;
      this.writeByte(Math.trunc((value / (0x100 ** byteCount)) & 0xFF));
    } while (byteCount > 0);
  }

  private writeIntBE(value: number, byteCount: number) {
    if (value < 0) {
      value = -1 - value;
      byteCount -= 1;
      this.writeByte(255 - Math.trunc(value / (0x100 ** byteCount) & 0x7F));
      do {
        byteCount -= 1;
        this.writeByte(255 - Math.trunc((value / (0x100 ** byteCount) & 0xFF)));
      } while (byteCount > 0);
    } else {
      byteCount -= 1;
      this.writeByte(Math.trunc((value / (0x100 ** byteCount) & 0x7F)));
      this.writeUIntBE(value, byteCount);
    }
  }

  public writeUuid(uuid: string) {
    // Example: 65617260-0299-4069-9717-cb0bf3a57b89
    const groups = uuid.split('-');
    const expectedGroupSizes = [4, 2, 2, 2, 6];
    for (let i = 0; i < groups.length; i++) {
      const group = safeArrayAccess(groups, i);
      invariant(
        group.length === safeArrayAccess(expectedGroupSizes, i) * 2,
        `Invalid Uuid: ${uuid}. Must be on the form xyzxyzxy-xyzx-xyzx-xyzx-xyzxyzxyzxyz`,
      );
      for (let j = 0; j < group.length; j += 2) {
        this.writeByte(parseInt(group.slice(j, j + 2), 16));
      }
    }
  }

  public writeUl(uuid: string) {
    // Example: cc.eb.04.e5.03.16.46.88.81.2f.dc.7b.2c.31.1e.ce
    this.writeBytes(new Uint8Array(uuid.split('.').map((s) => parseInt(s, 16))));
  }

  public writeString(s: string) {
    const textEncoder = new TextEncoder();
    this.writeBytes(textEncoder.encode(s).subarray(0, s.length));
  }

  public writeLocalTag(localTag: string) {
    // ab.cd
    if (localTag.length !== 5) {
      throw new Error('Not a local tag');
    }
    this.writeHex(localTag);
  }

  public writeHex(localTag: string) {
    // ab.cd.(...).ef
    for (const segment of localTag.split('.')) {
      invariant(segment.length === 2, `Invalid hex: ${localTag}`);
      this.writeUInt8(parseInt(segment, 16));
    }
  }

  public writeBerLength(ber: BerLength) {
    if (ber.bytesUsed < 1) {
      throw new Error(`Invalid bytesUsed for BerLength serialization: ${ber.bytesUsed}`);
    }

    if (ber.bytesUsed === 1) {
      this.writeByte(ber.length);
    } else {
      this.writeByte((ber.bytesUsed - 1) | 0x80);
      this.writeUIntBE(ber.length, ber.bytesUsed - 1);
    }
  }

  public writeFraction(fraction: Fraction) {
    this.writeUInt32BE(fraction.numerator);
    this.writeUInt32BE(fraction.denominator);
  }

  public writeWithSizePrefixUInt16BE(writePayload: (serializer: Serializer) => void) {
    const tmpTarget = this.getTmpTarget();
    writePayload(new Serializer(tmpTarget));
    const size = tmpTarget.size();

    this.writeUInt16BE(size);
    this.writeBytes(tmpTarget.getArray());
  }

  private tmpTarget : ArraySerializerTarget | null = null;
  private getTmpTarget(): ArraySerializerTarget {
    if (this.tmpTarget === null) {
      this.tmpTarget = new ArraySerializerTarget();
    } else {
      this.tmpTarget.clear();
    }
    return this.tmpTarget;
  }
}

export class ArraySerializerTarget implements SerializerTarget {
  private array : Uint8Array;
  private offset = 0;
  constructor(initialSize?: number) {
    this.array = new Uint8Array(initialSize ?? 0);
  }

  writeByte(byte: number) {
    if (this.array.length <= this.offset) {
      this.reallocate(this.array.length * 2);
    }
    this.array[this.offset] = byte;
    this.offset += 1;
  }

  writeBytes(array: Uint8Array) {
    if (this.array.length < this.offset + array.length) {
      const required = this.offset + array.length;
      const double = this.array.length * 2;
      this.reallocate(Math.max(required, double));
    }
    this.array.set(array, this.offset);
    this.offset += array.length;
  }

  size(): number {
    return this.offset;
  }

  getArray(): Uint8Array {
    return this.array.slice(0, this.offset);
  }

  clear() {
    this.offset = 0;
  }

  private reallocate(size: number) {
    const newArray = new Uint8Array(Math.max(size, 256));
    newArray.set(this.array, 0);
    this.array = newArray;
  }
}

